import React, { Component } from 'react'
import { Link, Prompt } from 'react-router-dom'
import { observer } from 'mobx-react'
import { toJS } from 'mobx'
import QRCodeReact from 'qrcode.react'
import uid from 'uuid'

import { NavInSite } from '../../components/nav'
import Loading from '../../components/loading'
import * as api from '../../lib/api'
import { Form } from '../../components/form'
import { Page } from '../'
import Picture from '../../components/picture'
import { moduleLabel, trackLabel, apiCache } from '../../stores'

const ExperienceList = ({ experiences, siteUid, prodStatus, onDropExperience, dropGroupId, emptyLabel, canEdit }) => {
  const [isDragOver, setIsDragOver] = React.useState(false)
  const dragDepth = React.useRef(0)
  const list = experiences || []
  const isEmpty = list.length === 0
  const handleDragOver = event => {
    if (!canEdit) return
    event.preventDefault()
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move'
    }
  }
  const handleDragEnter = event => {
    if (!canEdit) return
    event.preventDefault()
    dragDepth.current += 1
    setIsDragOver(true)
  }
  const handleDragLeave = event => {
    if (!canEdit) return
    event.preventDefault()
    dragDepth.current -= 1
    if (dragDepth.current <= 0) {
      dragDepth.current = 0
      setIsDragOver(false)
    }
  }
  const handleDrop = event => {
    if (!canEdit) return
    event.preventDefault()
    dragDepth.current = 0
    setIsDragOver(false)
    if (!onDropExperience) return
    const experienceId = event.dataTransfer.getData('text/experience-id') || event.dataTransfer.getData('text/plain')
    if (experienceId) {
      onDropExperience(experienceId, dropGroupId)
    }
  }
  return (
    <div
      className={`experience-preview-container${isDragOver ? ' is-drop-target' : ''}`}
      onDragOver={canEdit ? handleDragOver : undefined}
      onDragEnter={canEdit ? handleDragEnter : undefined}
      onDragLeave={canEdit ? handleDragLeave : undefined}
      onDrop={canEdit ? handleDrop : undefined}
      style={isEmpty ? { minHeight: 40 } : null}
    >
      {list.map(xp => {
        const isLoaded = typeof prodStatus?.[xp._id] === 'boolean'
        const isReleased = !!prodStatus?.[xp._id]
        return (
          <Link
            to={'/site/' + siteUid + '/experience/' + xp._id}
            className="experience-preview"
            key={xp._id}
            draggable={!!canEdit}
            onDragStart={
              canEdit
                ? event => {
                    event.dataTransfer.setData('text/experience-id', xp._id)
                    event.dataTransfer.setData('text/plain', xp._id)
                    event.dataTransfer.effectAllowed = 'move'
                  }
                : undefined
            }
          >
            <h3>
              {isLoaded && <span style={{ fontSize: 12 }}>{isReleased ? '🟢 ' : '🟡 '}</span>}
              {`${xp.name} - ${xp.locale}`}
            </h3>
            <div style={{ minHeight: 100 }}>
              <Picture resourceId={xp.picture} />
            </div>
          </Link>
        )
      })}
      {isEmpty && emptyLabel && <div style={{ opacity: 0.6, padding: '0.5em' }}>{emptyLabel}</div>}
    </div>
  )
}

// Identifiant du formulaire, qui permet aux boutons de la page de le soumettre depuis l'extérieur.
const FORM_ID = 'site-form'

class SiteForm extends Component {
  groupInputRefs = {}

  state = {
    error: '',
    saving: false,
    loading: false,
    creating: false,
    site: {},
    groups: {},
    groupsDirty: false,
    groupList: [],
    experienceGroupOverrides: {},
    experienceGroupsDirty: false,
    experiences: [],
    prodStatus: {},
    isSiteAdmin: false,
    editing: false,
    formKey: 0
  }

  isAdminForSite = (me, siteUid) => {
    if (!me || !siteUid) return false
    if (me.scopes?.includes('admin')) return true
    return (
      me.scopes?.some(scope => {
        const parts = scope?.split('/')
        return parts?.[0] === 'site' && parts[1] === siteUid && ['owner', 'admin'].includes(parts[2])
      }) || false
    )
  }

  canEditGroups = () => {
    const { site, isSiteAdmin, editing } = this.state
    return !!isSiteAdmin && !!site && !!site._id && !!editing
  }

  // Sortie du mode édition : tout ce qui n'a pas été enregistré est abandonné. Le formulaire garde
  // ses saisies dans son propre état, il est donc remonté par un changement de clé.
  cancelEdit = () => {
    const groups = this.state.site?.groups || {}
    this.setState(prevState => ({
      editing: false,
      formKey: prevState.formKey + 1,
      groups,
      groupsDirty: false,
      groupList: this.buildGroupList(groups),
      experienceGroupOverrides: {},
      experienceGroupsDirty: false
    }))
  }

  buildGroupList = groups => {
    return Object.entries(groups || {})
      .sort(([, nameA], [, nameB]) => {
        return String(nameA || '').localeCompare(String(nameB || ''), 'fr', { sensitivity: 'base' })
      })
      .map(([id, name]) => ({ id, name }))
  }

  hasUnsavedChanges = () => {
    return this.state.groupsDirty || this.state.experienceGroupsDirty
  }

  getEffectiveGroupId = (experience, overrides = this.state.experienceGroupOverrides) => {
    if (overrides && Object.prototype.hasOwnProperty.call(overrides, experience._id)) {
      return overrides[experience._id]
    }
    return experience.groupId || null
  }

  handleDropExperience = (experienceId, nextGroupId) => {
    if (!this.canEditGroups()) {
      return
    }
    const { siteUid } = this.props.match.params
    const experiences = apiCache.experiences.getFrom('sites', siteUid) || []
    const experience = experiences.find(xp => xp._id === experienceId)
    if (!experience) {
      return
    }
    const normalizedNext = nextGroupId || null
    const current = this.getEffectiveGroupId(experience)
    if ((current || null) === normalizedNext) {
      return
    }
    const baseGroupId = experience.groupId || null
    this.setState(prevState => {
      const nextOverrides = { ...(prevState.experienceGroupOverrides || {}) }
      if (normalizedNext === baseGroupId) {
        delete nextOverrides[experienceId]
      } else {
        nextOverrides[experienceId] = normalizedNext
      }
      const hasOverrides = Object.keys(nextOverrides).length > 0
      return {
        experienceGroupOverrides: nextOverrides,
        experienceGroupsDirty: hasOverrides
      }
    })
  }

  getExperienceGroupUpdates = () => {
    const { siteUid } = this.props.match.params
    const experiences = apiCache.experiences.getFrom('sites', siteUid) || []
    const overrides = this.state.experienceGroupOverrides || {}
    return experiences.reduce((acc, xp) => {
      if (Object.prototype.hasOwnProperty.call(overrides, xp._id)) {
        acc.push({ _id: xp._id, groupId: overrides[xp._id] || null })
      }
      return acc
    }, [])
  }

  addGroup = () => {
    if (!this.canEditGroups()) {
      return
    }
    const { site, groups } = this.state
    if (!site || !site._id) {
      return
    }
    const nextGroups = { ...(groups || {}) }
    const groupId = typeof uid.v4 === 'function' ? uid.v4() : uid.v1()
    nextGroups[groupId] = 'Nouveau groupe'
    this.setState(
      prevState => ({
        groups: nextGroups,
        groupsDirty: true,
        groupList: [{ id: groupId, name: 'Nouveau groupe' }, ...(prevState.groupList || [])]
      }),
      () => {
        const input = this.groupInputRefs[groupId]
        if (input) {
          input.focus()
          input.select()
        }
      }
    )
  }

  onUnload = event => {
    if (this.hasUnsavedChanges()) {
      event.preventDefault()
      event.returnValue =
        'Vous avez des modifications non enregistrées.\nSouhaitez vous quitter cette page et perdre les modifications non enregistrée ? '
      return event.returnValue
    }
  }

  removeGroup = groupId => {
    if (!this.canEditGroups()) {
      return
    }
    const { site, groups } = this.state
    if (!site || !site._id || !groups || !Object.prototype.hasOwnProperty.call(groups, groupId)) {
      return
    }
    const nextGroups = { ...groups }
    delete nextGroups[groupId]
    const { siteUid } = this.props.match.params
    const experiences = apiCache.experiences.getFrom('sites', siteUid) || []
    this.setState(prevState => {
      const nextOverrides = { ...(prevState.experienceGroupOverrides || {}) }
      experiences.forEach(xp => {
        const effectiveGroupId = this.getEffectiveGroupId(xp, nextOverrides)
        if (effectiveGroupId === groupId) {
          const baseGroupId = xp.groupId || null
          if (baseGroupId === null) {
            delete nextOverrides[xp._id]
          } else {
            nextOverrides[xp._id] = null
          }
        }
      })
      const hasOverrides = Object.keys(nextOverrides).length > 0
      return {
        groups: nextGroups,
        groupsDirty: true,
        groupList: (prevState.groupList || []).filter(group => group.id !== groupId),
        experienceGroupOverrides: nextOverrides,
        experienceGroupsDirty: hasOverrides
      }
    })
  }

  updateGroupName = (groupId, name) => {
    if (!this.canEditGroups()) {
      return
    }
    const { site, groups } = this.state
    if (!site || !site._id || !groups || !Object.prototype.hasOwnProperty.call(groups, groupId)) {
      return
    }
    const nextGroups = { ...groups, [groupId]: name }
    this.setState(prevState => ({
      groups: nextGroups,
      groupsDirty: true,
      groupList: (prevState.groupList || []).map(group => (group.id === groupId ? { ...group, name } : group))
    }))
  }

  async createScenario(name, modules, experienceId, onEnd) {
    const scenario = await api.scenarios.create({ name, experienceId, onEnd })
    for (let order = 0; order < modules.length; order++) {
      await api.modules.create({ ...modules[order], scenarioId: scenario._id, experienceId, order })
    }
    return scenario
  }

  async createPoint(name, gameModules, qrcode, track, onEnd, order) {
    const { experienceId, _id: trackId } = track

    const clues = await this.createScenario(
      name + ' - indices',
      [{ type: 'gallery', title: 'Indices', canGoBack: true }],
      experienceId
    )
    const games = await this.createScenario(name + ' - jeu', gameModules, experienceId, onEnd)
    return api.points.create({
      name,
      trackId,
      location: [46 + Math.random(), 4 + Math.random()],
      active: true,
      visible: true,
      order,
      check: {
        type: 'qrcode',
        code: qrcode, // 'https://explorgames.com/qrcode/0' + index,
        clue: clues._id,
        scenario: games._id
      },
      experienceId
    })
  }

  createAdventureTrack = async xp => {
    const { _id: experienceId } = xp
    const adventureTrack = await api.tracks.create({
      experienceId,
      type: 'adventure',
      minZoom: 14,
      maxZoom: 18,
      name: 'Aventure'
    })
    xp.startTrack = adventureTrack._id
    const scenariosInButton = []
    await api.experiences.upsert(xp)
    for (let index = 1; index < 9; index++) {
      const point = await this.createPoint(
        'Point 0' + index,
        [
          { type: 'conversation', title: 'avant jeu', canGoBack: false },
          { type: 'markdown', title: 'jeu' },
          { type: 'score_conversation', title: 'après jeu' }
        ],
        'https://explorgames.com/qrcode/0' + index,
        adventureTrack,
        [],
        index
      )
      scenariosInButton.push(point.check.scenario)
    }

    let point = await this.createPoint(
      'Fin',
      [
        { type: 'conversation', title: 'avant jeu de fin', canGoBack: false },
        { type: 'markdown', title: 'jeu de fin' },
        { type: 'score_conversation', title: 'score global', scoreType: 'experience' },
        { type: 'selfie', title: 'Selfie' },
        {
          type: 'rating',
          title: 'Notation',
          ratings: [
            { key: 'fun', label: 'Avez-vous passé un bon moment ?' },
            { key: 'difficulty', label: 'Est-ce que le niveau de difficulté est adapté ?' },
            { key: 'next', label: 'Êtes-vous prêts à embarquer à nouveau ? ' }
          ]
        },
        {
          type: 'markdown',
          title: 'Crédits',
          code: `# Crédits

* ExplorGames® Découverte
* Création et scénario original: La fine équipe d’Atelier Nature 
* Contenus historiques :  
* Ecriture des textes: 
* Illustrateur : 
* Images : Freepix - pixabay
* Voix : 
* Musique : 
      `
        },
        { type: 'gallery', title: 'Parternaires' }
      ],
      'https://explorgames.com/qrcode/13',
      adventureTrack,
      [{ type: 'disable_all_points' }],
      10
    )

    scenariosInButton.push(point.check.scenario)

    const hordeSuccess = await this.createScenario(
      'Horde succes',
      [{ type: 'score_conversation', title: 'horde - réussite', scoreType: 'track' }],
      experienceId,
      [{ type: 'load_track', track: adventureTrack._id }]
    )
    scenariosInButton.push(hordeSuccess._id)

    const hordeTrack = await api.tracks.create({
      experienceId,
      type: 'horde',
      minZoom: 14,
      maxZoom: 18,
      name: 'Horde'
    })

    point = await api.points.create({
      name: 'objectif horde ',
      trackId: hordeTrack._id,
      location: [46 + Math.random(), 4 + Math.random()],
      experienceId: xp._id,
      scenario: hordeSuccess._id,
      distance: 15,
      active: true,
      visible: true
    })

    point = await this.createPoint(
      'Horde',
      [{ type: 'conversation', title: 'avant horde' }],
      'https://explorgames.com/qrcode/10',
      adventureTrack,
      [{ type: 'load_track', track: adventureTrack._id }],
      11
    )
    scenariosInButton.push(point.check.scenario)

    const chaseSuccess = await this.createScenario(
      'chase succes',
      [{ type: 'score_conversation', title: 'chase - réussite', scoreType: 'track' }],
      experienceId,
      [{ type: 'load_track', track: adventureTrack._id }]
    )
    scenariosInButton.push(chaseSuccess._id)

    const chaseFailure = await this.createScenario(
      'Course poursuite - echec',
      [{ type: 'score_conversation', title: 'Course poursuite - echec', scoreType: 'track' }],
      experienceId,
      [{ type: 'load_track', track: adventureTrack._id }]
    )
    scenariosInButton.push(chaseFailure._id)

    const chaseTrack = await api.tracks.create({
      experienceId,
      type: 'chase',
      name: 'Course Poursuite',
      minZoom: 14,
      maxZoom: 18,
      entities: [{ _id: uid.v1(), speed: 1 }],
      failure: chaseFailure._id
    })
    point = await this.createPoint(
      'Chase',
      [{ type: 'conversation', title: 'avant chase' }],
      'https://explorgames.com/qrcode/11',
      adventureTrack,
      [{ type: 'load_track', track: chaseTrack._id }],
      12
    )
    scenariosInButton.push(point.check.scenario)

    await api.points.create({
      name: 'objectif course poursuite ',
      trackId: chaseTrack._id,
      location: [46 + Math.random(), 4 + Math.random()],
      experienceId: xp._id,
      scenario: chaseSuccess._id,
      distance: 15,
      active: true,
      visible: true
    })

    const endPhotoHunt = await this.createScenario(
      'Rally Photo fin',
      [{ type: 'score_conversation', title: 'chase - réussite', scoreType: 'track' }],
      experienceId,
      [{ type: 'load_track', track: adventureTrack._id }]
    )
    scenariosInButton.push(endPhotoHunt._id)

    const photoHuntTrack = await api.tracks.create({
      experienceId,
      type: 'photo_hunt',
      name: 'Rally Photo',
      minZoom: 14,
      maxZoom: 18,
      shufflePoints: false,
      allowNavigation: false,
      scenario: endPhotoHunt._id
    })
    await this.createPoint(
      'Rally Photo',
      [{ type: 'conversation', title: 'avant Rally Photo' }],
      'https://explorgames.com/qrcode/12',
      adventureTrack,
      [{ type: 'load_track', track: photoHuntTrack._id }],
      13
    )

    for (let order = 1; order < 4; order++) {
      await api.points.create({
        name: 'Rallye Photo - point ' + order,
        trackId: photoHuntTrack._id,
        location: [46 + Math.random(), 4 + Math.random()],
        experienceId: xp._id,
        active: true,
        visible: true,
        clue: "Allez à l'endroit où cette photo a été prise et validez votre position !",
        validation: { type: 'location', distance: 15 }
      })
    }
    const menu = []
    const tuto = await this.createScenario(
      'Tutoriel',
      [
        {
          type: 'conversation',
          title: 'tutoriel',
          dialogs: [
            {
              text: "Prêts à commencer une nouvelle aventure ?\nChers explorateurs, apprenons les bases ensemble, c'est parti !",
              position: 'bottom|start'
            },
            {
              text: 'Votre objectif principal est de retrouver les QR codes, cachés autour de vous.',
              position: 'bottom|start'
            },
            {
              text: 'Ils sont localisés sur la carte par des points, comme celui- ci.',
              position: 'bottom|start'
            },
            {
              text: 'Ce marqueur vous donne votre position sur la carte en temps réel.',
              position: 'bottom|start'
            },
            {
              text:
                'Pour vous aider, utilisez les indices photos et l’aiguille de la boussole qui vous indique la direction à suivre.',
              position: 'bottom|start'
            },
            {
              text: 'Lorsque vous avez trouvé le QR code, cliquez sur trouver pour le scanner. Et c’est parti !',
              position: 'bottom|start'
            },
            {
              text: 'Si vous avez besoin de retrouver ce tuto lors de votre aventure, cliquez sur cet icône.',
              position: 'bottom|start'
            }
          ]
        }
      ],
      experienceId,
      []
    )
    scenariosInButton.push(tuto._id)
    for (const scenarioId of scenariosInButton) {
      const scenario = await api.scenarios.get(scenarioId)
      menu.push({ label: scenario.name, action: { type: 'load_scenario', scenario: scenarioId } })
    }
    await api.tracks.update(adventureTrack._id, { menu })
    return adventureTrack
  }

  createExperienceFromTemplate = async () => {
    this.setState({ creating: true })
    const { siteUid: siteId } = this.props.match.params
    const xp = await api.experiences.create({
      siteId,
      locale: 'fr',
      name: new Date(),
      description: 'créer par modèle',
      authorizations: { tracks: toJS(Object.keys(trackLabel)), modules: toJS(Object.keys(moduleLabel)) },
      waitForDownload: true,
      useProgression: true
    })
    // scenario d'intro
    const intro = this.createScenario(
      'Intro',
      [
        { type: 'video', title: 'Teaser' },
        { type: 'conversation', title: 'Introduction' }
      ],
      xp._id
    )
    xp.onStart = [{ type: 'load_scenario', scenario: intro._id }]

    // parcours
    const adventureTrack = await this.createAdventureTrack(xp)
    xp.startTrack = adventureTrack._id

    api.experiences.upsert(xp)

    this.setState({ creating: false })
    this.props.history.push('/site/' + siteId + '/experience/' + xp._id)
    // photo_hunt
  }

  save = formData => {
    const { siteUid } = this.props.match.params
    const { groups } = this.state
    const nextFormData = { ...formData, groups: groups || {} }
    return api.sites.upsert(nextFormData).then(async site => {
      const experienceUpdates = this.getExperienceGroupUpdates()
      if (experienceUpdates.length > 0) {
        await Promise.all(
          experienceUpdates.map(update => api.experiences.update(update._id, { groupId: update.groupId || null }))
        )
      }
      const savedGroups = site?.groups || groups || {}
      this.setState({
        site: site || nextFormData,
        groups: savedGroups,
        groupsDirty: false,
        groupList: this.buildGroupList(savedGroups),
        experienceGroupOverrides: {},
        experienceGroupsDirty: false,
        editing: false
      })
      if (!siteUid) {
        const url = '/site/' + site.uid
        this.props.history.push(url)
      }
    })
  }

  rollBack = patchId => {
    const { siteUid } = this.props.match.params
    return api.sites.rollBackToPatch(siteUid, patchId).then(site =>
      this.setState({
        site,
        groups: site?.groups || {},
        groupsDirty: false,
        groupList: this.buildGroupList(site?.groups || {}),
        experienceGroupOverrides: {},
        experienceGroupsDirty: false
      })
    )
  }

  async componentDidMount() {
    window.addEventListener('beforeunload', this.onUnload)
    const siteUid = this.props.match.params.siteUid
    if (!siteUid) {
      return
    }
    this.setState({ loading: true })
    const experiences = await api.experiences.getFrom('sites', siteUid)
    const site = await api.sites.get(siteUid)
    const me = await api.users.me()
    const isSiteAdmin = this.isAdminForSite(me, siteUid)
    this.setState({
      loading: false,
      isSiteAdmin,
      site,
      experiences,
      groups: site?.groups || {},
      groupsDirty: false,
      groupList: this.buildGroupList(site?.groups || {}),
      experienceGroupOverrides: {},
      experienceGroupsDirty: false
    })

    // Requête asynchrone qui actualisera le state "prodStatus" en fin de traitement, sans bloquer l'affichage.
    api.releases
      .listFromSite('prod', siteUid) // Liste des expériences en prod.
      .then(experiences => (experiences || []).map(exp => exp._id)) // Extraction de l'ID
      .then(expIds => {
        // Mise à jour du state "prodStatus", en indiquant pour chaque expérience si elle est dans la liste d'IDs.
        const prodStatus = experiences?.reduce((acc, exp) => ({ ...acc, [exp._id]: expIds.includes(exp._id) }), {}) || {}
        this.setState({ prodStatus })
      })
      .catch(() => {}) // En cas d'erreur il ne se passe rien, le state n'est juste pas actualisé.
  }

  componentWillUnmount() {
    window.removeEventListener('beforeunload', this.onUnload)
  }

  /* mobx will force a new render */
  componentWillReact() {
    const { siteUid } = this.props.match.params
    if (siteUid && this.state.site) {
      const site = toJS(apiCache.sites.get(siteUid))
      if (site && this.state.site.updatedAt !== site.updatedAt) {
        this.setState(prevState => {
          const nextState = { site }
          if (!prevState.groupsDirty) {
            const nextGroups = site?.groups || {}
            nextState.groups = nextGroups
            nextState.groupList = this.buildGroupList(nextGroups)
          }
          if (!prevState.experienceGroupsDirty) {
            nextState.experienceGroupOverrides = {}
          }
          return nextState
        })
      }
    }
  }

  delete = () => {
    const siteUid = this.props.match.params.siteUid
    return api.sites.remove(siteUid).then(() => this.props.history.push('/'))
  }

  renderSiteIdentity() {
    const { site, editing, formKey } = this.state
    const { siteUid } = this.props.match.params
    const locked = !!siteUid && !editing

    return (
      <Form
        key={formKey}
        formId={FORM_ID}
        formPath="site"
        initialFormData={site}
        disabled={locked}
        onDelete={this.delete}
        onSubmit={this.save}
        listHistory={() => api.sites.listAvailableRollBack(siteUid)}
        rollBackToPatch={this.rollBack}
      />
    )
  }

  render() {
    const { loading, site, creating, groupList, experienceGroupOverrides, editing } = this.state
    const { siteUid } = this.props.match.params
    siteUid && apiCache.sites.get(siteUid) // to inform mobx I need to watch this in this component
    const experiences = (apiCache.experiences.getFrom('sites', siteUid) || []).sort((xp1, xp2) => {
      return xp1.name + '-' + xp1.locale - (xp2.name + '-' + xp2.locale)
    })
    const canEditGroups = this.canEditGroups()
    const hasGroups = (groupList || []).length > 0
    const showGroupsSection = canEditGroups || hasGroups
    const groupIds = new Set((groupList || []).map(group => group.id))
    const groupedExperiences = (groupList || []).reduce((acc, group) => {
      acc[group.id] = []
      return acc
    }, {})
    const ungroupedExperiences = []
    experiences.forEach(xp => {
      const groupId = this.getEffectiveGroupId(xp, experienceGroupOverrides)
      if (groupId && groupIds.has(groupId)) {
        groupedExperiences[groupId].push(xp)
      } else {
        ungroupedExperiences.push(xp)
      }
    })
    return (
      <Page>
        <NavInSite />
        <div id="content">
          {loading && <Loading />}
          {!loading && this.renderSiteIdentity()}
          {!loading && showGroupsSection && (
            <div>
              <h4 style={{ display: 'inline-block', marginRight: 8 }}>Groupes</h4>
              {canEditGroups && (
                <button className="btn btn-info btn-add" onClick={this.addGroup} title="Ajouter un groupe" />
              )}

              {/* {(!groupList || groupList.length === 0) && <div style={{ opacity: 0.7, marginTop: 6 }}>Aucun groupe</div>} */}
              {(groupList || []).map(group => (
                <div key={group.id} style={{ marginTop: 8 }}>
                  <input
                    className="form-control input-sm"
                    style={{ display: 'inline-block', width: 240, marginRight: 8 }}
                    value={group.name}
                    disabled={!canEditGroups}
                    ref={el => {
                      if (el) {
                        this.groupInputRefs[group.id] = el
                      } else {
                        delete this.groupInputRefs[group.id]
                      }
                    }}
                    onChange={e => this.updateGroupName(group.id, e.target.value)}
                  />
                  {canEditGroups && (
                    <button
                      className="btn btn-danger btn-xs"
                      style={{ marginLeft: 8 }}
                      onClick={() => this.removeGroup(group.id)}
                    >
                      Supprimer
                    </button>
                  )}
                  <div style={{ marginTop: 0 }}>
                    <ExperienceList
                      experiences={groupedExperiences[group.id]}
                      siteUid={siteUid}
                      prodStatus={this.state.prodStatus}
                      onDropExperience={canEditGroups ? this.handleDropExperience : null}
                      dropGroupId={group.id}
                      emptyLabel={canEditGroups ? 'Déposer une expérience ici' : null}
                      canEdit={canEditGroups}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
          <Prompt
            when={this.hasUnsavedChanges()}
            message="Vous avez des modifications non enregistrées.\nSouhaitez vous quitter cette page et perdre les modifications non enregistrée ? "
          />
          <h4 style={{ display: 'inline-block', marginTop: 18 }}>
            Expériences {groupList?.length > 0 ? '(hors groupe)' : ''}
          </h4>
          <ExperienceList
            experiences={ungroupedExperiences}
            siteUid={siteUid}
            prodStatus={this.state.prodStatus}
            onDropExperience={canEditGroups ? this.handleDropExperience : null}
            dropGroupId={null}
            emptyLabel={canEditGroups ? 'Déposer ici pour enlever le groupe' : null}
            canEdit={canEditGroups}
          />
          <h4>QrCode à scanner pour ouvrir ce site directement dans l'application explorgames</h4>
          <QRCodeReact
            value={'https://workshop.explor.games/site/' + site._id}
            style={{ padding: '10px' }}
            size={148}
            includeMargin
            level="M"
          />

          {creating && siteUid && (
            <button className="btn btn-default btn-block" disabled>
              {' '}
              Création en cours{' '}
            </button>
          )}
          {!creating && siteUid && (
            <button className="btn btn-default btn-block" onClick={this.createExperienceFromTemplate}>
              {' '}
              Créer un explorgame{' '}
            </button>
          )}
          {!loading && (
            <div style={{ position: 'fixed', bottom: 5, right: 5, display: 'flex', gap: 8 }}>
              {!siteUid ? (
                <button type="submit" form={FORM_ID} className="btn btn-primary">
                  Créer
                </button>
              ) : editing ? (
                <>
                  <button type="button" className="btn btn-default" onClick={this.cancelEdit}>
                    <i className="fa fa-times" style={{ marginRight: 8 }} />
                    Annuler
                  </button>
                  <button type="submit" form={FORM_ID} className="btn btn-primary">
                    Enregistrer les modifications
                  </button>
                </>
              ) : (
                <button type="button" className="btn btn-info" onClick={() => this.setState({ editing: true })}>
                  <i className="fa fa-pencil" style={{ marginRight: 8 }} />
                  Modifier ce site
                </button>
              )}
            </div>
          )}
        </div>
        <div id="app-preview" />
      </Page>
    )
  }
}
export default observer(SiteForm)
